"use client";

import { useEffect } from "react";
import posthog from "posthog-js";

/**
 * Global error boundary. Renders only when the root layout itself throws, so
 * brand CSS variables may not be loaded — colors here use inline neutral hex.
 *
 * The inner presentational body is exported separately as `GlobalErrorBody`
 * so tests can render it inside JSDOM's existing `<body>` without nesting a
 * second `<html>`.
 */
export function GlobalErrorBody({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    posthog.captureException(error);
  }, [error]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        backgroundColor: "#ffffff",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "28rem",
          borderRadius: "1rem",
          padding: "2rem",
          textAlign: "center",
          border: "1px solid #e5e7eb",
          backgroundColor: "#ffffff",
        }}
      >
        <h1
          style={{
            marginBottom: "0.75rem",
            fontSize: "1.5rem",
            fontWeight: 700,
            color: "#111827",
          }}
        >
          Something went wrong
        </h1>
        <p
          style={{
            marginBottom: "1.5rem",
            fontSize: "1rem",
            color: "#6b7280",
          }}
        >
          The TrustedOnly team has been notified and is working on resolving it.
          Please try again in a moment.
        </p>
        <button
          onClick={reset}
          style={{
            display: "inline-flex",
            height: "2.5rem",
            alignItems: "center",
            borderRadius: "9999px",
            padding: "0 1.25rem",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "#ffffff",
            backgroundColor: "#111827",
            border: "none",
            cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  return (
    <html>
      <body>
        <GlobalErrorBody error={error} reset={reset} />
      </body>
    </html>
  );
}
